<template>
    <div v-if="stats" class="flashcard-stats">
        <h3 class="stats-title">Study Progress</h3>

        <!-- Card State Distribution -->
        <div class="state-distribution">
            <div class="state-bar">
                <div
                    v-if="stats.card_states.review > 0"
                    class="state-segment state-review"
                    :style="{ width: segmentWidth('review') }"
                    :title="`${stats.card_states.review} review`"
                />
                <div
                    v-if="stats.card_states.learning + stats.card_states.relearning > 0"
                    class="state-segment state-learning"
                    :style="{ width: segmentWidth('learning') }"
                    :title="`${stats.card_states.learning + stats.card_states.relearning} learning`"
                />
                <div
                    v-if="stats.card_states.new > 0"
                    class="state-segment state-new"
                    :style="{ width: segmentWidth('new') }"
                    :title="`${stats.card_states.new} new`"
                />
            </div>
            <div class="state-legend">
                <span class="legend-item">
                    <span class="legend-dot legend-dot-review" />
                    {{ stats.card_states.review }} mastered
                </span>
                <span class="legend-item">
                    <span class="legend-dot legend-dot-learning" />
                    {{ stats.card_states.learning + stats.card_states.relearning }} learning
                </span>
                <span class="legend-item">
                    <span class="legend-dot legend-dot-new" />
                    {{ stats.card_states.new }} new
                </span>
            </div>
        </div>

        <!-- Key Metrics -->
        <div class="metrics-row">
            <div v-if="stats.average_retention !== null" class="metric">
                <span class="metric-value">{{ Math.round(stats.average_retention * 100) }}%</span>
                <span class="metric-label">Retention</span>
            </div>
            <div class="metric">
                <span class="metric-value">{{ stats.total_reviews }}</span>
                <span class="metric-label">Total Reviews</span>
            </div>
            <div class="metric">
                <span class="metric-value">{{ stats.cards_due_today }}</span>
                <span class="metric-label">Due Today</span>
            </div>
            <div v-if="stats.leech_count > 0" class="metric metric-warning">
                <span class="metric-value">{{ stats.leech_count }}</span>
                <span class="metric-label">Difficult Cards</span>
            </div>
        </div>

        <!-- Review Forecast -->
        <div v-if="stats.review_forecast && stats.review_forecast.length" class="forecast">
            <p class="forecast-title">Next 7 Days</p>
            <div class="forecast-bars">
                <div
                    v-for="day in stats.review_forecast"
                    :key="day.date"
                    class="forecast-day"
                >
                    <div
                        class="forecast-bar"
                        :style="{ height: forecastBarHeight(day.count) }"
                    />
                    <span class="forecast-count">{{ day.count }}</span>
                    <span class="forecast-label">{{ formatDate(day.date) }}</span>
                </div>
            </div>
        </div>
    </div>

    <div v-else-if="loading" class="flashcard-stats-loading">
        <e-loading :is-full-page="false" active />
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import flashcardService from '@/services/flashcardService.js';

const props = defineProps({
    activityId: {
        type: [Number, String],
        required: true,
    },
});

const stats = ref(null);
const loading = ref(true);

onMounted(async () => {
    try {
        stats.value = await flashcardService.getStats(props.activityId);
    } catch {
        // Stats not available (FSRS not enabled or error)
        stats.value = null;
    } finally {
        loading.value = false;
    }
});

const totalCards = computed(() => {
    if (!stats.value) return 0;
    const s = stats.value.card_states;
    return s.new + s.learning + s.review + s.relearning;
});

const maxForecast = computed(() => {
    if (!stats.value?.review_forecast) return 1;
    return Math.max(1, ...stats.value.review_forecast.map(d => d.count));
});

function segmentWidth(type) {
    if (!stats.value || totalCards.value === 0) return '0%';
    const s = stats.value.card_states;
    let count;
    if (type === 'review') count = s.review;
    else if (type === 'learning') count = s.learning + s.relearning;
    else count = s.new;
    return `${Math.max(0, (count / totalCards.value) * 100)}%`;
}

function forecastBarHeight(count) {
    return `${Math.max(4, (count / maxForecast.value) * 60)}px`;
}

function formatDate(dateStr) {
    const date = new Date(`${dateStr}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((date - today) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tmrw';
    return date.toLocaleDateString('en', { weekday: 'short' });
}
</script>

<style lang="scss" scoped>
@import '~assets/scss/toolbox.scss';

.flashcard-stats {
    background: $white-ter;
    border-radius: 12px;
    padding: 1.25rem;
    margin-top: 1rem;
}

.stats-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: $grey;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 1rem;
}

.state-distribution {
    margin-bottom: 1rem;
}

.state-bar {
    display: flex;
    height: 8px;
    border-radius: 4px;
    overflow: hidden;
    gap: 2px;
    margin-bottom: 0.5rem;
}

.state-segment {
    border-radius: 4px;
    transition: width 0.5s ease;
    min-width: 4px;
}

.state-review { background: $success; }
.state-learning { background: $warning; }
.state-new { background: $info; }

.state-legend {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
}

.legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.8rem;
    color: $grey;
}

.legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
}

.legend-dot-review { background: $success; }
.legend-dot-learning { background: $warning; }
.legend-dot-new { background: $info; }

.metrics-row {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
}

.metric {
    flex: 1;
    min-width: 70px;
    text-align: center;
    padding: 0.5rem;
    background: $white;
    border-radius: 8px;
}

.metric-warning .metric-value {
    color: $danger;
}

.metric-value {
    font-size: 1.25rem;
    font-weight: 600;
    color: $primary;
    line-height: 1.2;
}

.metric-label {
    font-size: 0.7rem;
    color: $grey-light;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.forecast {
    margin-top: 0.5rem;
}

.forecast-title {
    font-size: 0.8rem;
    font-weight: 500;
    color: $grey;
    margin-bottom: 0.5rem;
}

.forecast-bars {
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;
    height: 90px;
}

.forecast-day {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 0.15rem;
}

.forecast-bar {
    width: 100%;
    max-width: 32px;
    background: $primary;
    border-radius: 4px 4px 0 0;
    opacity: 0.7;
    transition: height 0.3s ease;
}

.forecast-count {
    font-size: 0.7rem;
    font-weight: 600;
    color: $grey-dark;
}

.forecast-label {
    font-size: 0.65rem;
    color: $grey-light;
}
</style>
